import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Search, 
  X, 
  User, 
  FileText, 
  FolderOpen,
  Loader,
  Users,
  Clock
} from 'lucide-react';
import { userAPI, timesheetAPI, projectAPI } from '../services/api';

const GlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleValue = typeof user?.role === 'object' ? user?.role?.name : user?.role;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global search query - using optimized backend endpoint
  const { data: searchResults, isLoading } = useQuery(
    ['global-search', debouncedSearchTerm, roleValue],
    async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
        return { users: [], timesheets: [], projects: [] };
      }

      try {
        let response;
        if (roleValue === 'MANAGER') {
          response = await projectAPI.getManagerGlobalSearch(debouncedSearchTerm);
        } else {
          response = await userAPI.globalSearch(debouncedSearchTerm);
        }
        return {
          users: response?.data?.users || [],
          timesheets: response?.data?.timesheets || [],
          projects: response?.data?.projects || []
        };
      } catch (error) {
        console.error('Error in global search:', error);
        return { users: [], timesheets: [], projects: [] };
      }
    },
    {
      enabled: debouncedSearchTerm.length >= 2,
      staleTime: 10000, // Cache for 10 seconds
      cacheTime: 30000, // Keep in cache for 30 seconds
    }
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleUserClick = (userId) => {
    if (roleValue === 'SUPER_ADMIN') {
      navigate(`/super-admin/users/${userId}/timesheets`);
    } else if (roleValue === 'ADMIN') {
      navigate(`/admin/users/${userId}/timesheets`);
    } else if (roleValue === 'MANAGER') {
      navigate(`/manager/users/${userId}/timesheets`);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleTimesheetClick = (timesheetId) => {
    if (roleValue === 'SUPER_ADMIN') {
      navigate(`/super-admin/timesheets/${timesheetId}`);
    } else if (roleValue === 'ADMIN') {
      navigate(`/admin/timesheets/${timesheetId}`);
    } else if (roleValue === 'MANAGER') {
      navigate(`/manager/timesheets/${timesheetId}`);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleProjectClick = (projectId) => {
    if (roleValue === 'MANAGER') {
      navigate('/manager/projects');
    } else {
      navigate('/admin/projects');
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const totalResults = searchResults 
    ? (searchResults.users?.length || 0) + 
      (searchResults.timesheets?.length || 0) + 
      (searchResults.projects?.length || 0)
    : 0;

  return (
    <div className="relative w-full min-w-0 max-w-md">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
        </div>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={roleValue === 'MANAGER' ? 'Search team...' : 'Search users, timesheets...'}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setIsOpen(true)}
          className="block w-full min-w-0 pl-9 pr-9 md:pl-10 md:pr-10 py-2.5 md:py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setIsOpen(false);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
            aria-label="Clear search"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Dropdown Results - full width on mobile, fixed max height */}
      <AnimatePresence>
        {isOpen && debouncedSearchTerm.length >= 2 && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 md:right-0 md:left-auto mt-2 w-full md:w-96 bg-white rounded-xl md:rounded-lg shadow-xl border border-gray-200 z-50 max-h-[70vh] md:max-h-96 overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-sm font-semibold text-gray-900">Results</h3>
              {isLoading && (
                <Loader className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
              )}
            </div>

            {/* Results */}
            <div className="max-h-[60vh] md:max-h-80 overflow-y-auto overscroll-contain">
              {isLoading ? (
                <div className="px-4 py-8 md:py-8 text-center">
                  <Loader className="w-6 h-6 mx-auto text-gray-400 animate-spin" />
                  <p className="text-sm text-gray-500 mt-2">Searching...</p>
                </div>
              ) : totalResults === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Search className="w-10 h-10 md:w-8 md:h-8 mx-auto mb-3 md:mb-2 text-gray-300" />
                  <p className="text-sm font-medium text-gray-600">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                </div>
              ) : (
                <>
                  {/* Users / Team members Section */}
                  {searchResults?.users?.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2.5 bg-gray-50">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs font-semibold text-gray-700 uppercase">
                            {roleValue === 'MANAGER' ? 'Team members' : 'Users'} ({searchResults.users.length})
                          </span>
                        </div>
                      </div>
                      {searchResults.users.map((user) => (
                        <motion.div
                          key={user.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="px-4 py-3.5 md:py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-50 touch-manipulation"
                          onClick={() => handleUserClick(user.id)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="hidden md:block flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                                <span className="text-xs font-medium text-white">
                                  {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                </span>
                              </div>
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
                              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
                                <span className="text-xs text-gray-400">{user.role?.name || user.role}</span>
                                {user.employeeCode && (
                                  <>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-xs text-gray-400">ID: {user.employeeCode}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Timesheets Section - not shown for manager (team members only) */}
                  {searchResults?.timesheets?.length > 0 && roleValue !== 'MANAGER' && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2.5 bg-gray-50">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs font-semibold text-gray-700 uppercase">Timesheets ({searchResults.timesheets.length})</span>
                        </div>
                      </div>
                      {searchResults.timesheets.map((timesheet) => (
                        <motion.div
                          key={timesheet.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="px-4 py-3.5 md:py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-50 touch-manipulation"
                          onClick={() => handleTimesheetClick(timesheet.id)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="hidden md:block flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-purple-500 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-white" />
                              </div>
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {timesheet.employee?.firstName} {timesheet.employee?.lastName}
                              </p>
                              <p className="text-xs text-gray-500 truncate mt-0.5">
                                Timesheet #{timesheet.id}
                              </p>
                              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  timesheet.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                  timesheet.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  timesheet.status === 'SUBMITTED' || timesheet.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {timesheet.status}
                                </span>
                                {timesheet.startDate && (
                                  <>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-xs text-gray-400">
                                      {new Date(timesheet.startDate).toLocaleDateString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Projects Section - not shown for manager (team members only) */}
                  {searchResults?.projects?.length > 0 && roleValue !== 'MANAGER' && (
                    <div>
                      <div className="px-4 py-2.5 bg-gray-50">
                        <div className="flex items-center space-x-2">
                          <FolderOpen className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs font-semibold text-gray-700 uppercase">Projects ({searchResults.projects.length})</span>
                        </div>
                      </div>
                      {searchResults.projects.map((project) => (
                        <motion.div
                          key={project.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="px-4 py-3.5 md:py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-50 last:border-b-0 touch-manipulation"
                          onClick={() => handleProjectClick(project.id)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="hidden md:block flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                                <FolderOpen className="w-4 h-4 text-white" />
                              </div>
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {project.name}
                              </p>
                              {project.description && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {project.description}
                                </p>
                              )}
                            </div>
                            <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearch;
